import { enumerateDatesInRange } from '@/utils/performanceChartAxisTicks';
import type {
  PerformanceDataPoint,
  PerformanceGranularity,
} from '@/utils/performanceChartMetrics';
import { isoToLocalDate } from '@/utils/dateRangePicker';
import { toIsoDate } from '@/api/reportDateRanges';
import type { DateRange } from '@/api/reportDateRanges';
import { isRangeValid } from '@/api/reportDateRanges';

type DailyTotals = {
  sales: number;
  orders: number;
  spend: number;
  hasData: boolean;
};

function numberValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function inclusiveLocalDayCount(startDate: string, endDate: string): number {
  const dates = enumerateDatesInRange(startDate, endDate);
  return Math.max(1, dates.length);
}

export type PerformanceStreamRange = {
  startIso: string;
  endIso: string;
  dayCount: number;
  isValid: boolean;
};

export function buildPerformanceStreamRange(range: DateRange): PerformanceStreamRange {
  const start = isoToLocalDate(range.startDate);
  const end = isoToLocalDate(range.endDate);
  const valid = Boolean(start && end) && isRangeValid(range);
  if (!valid || !start || !end) {
    return {
      startIso: new Date().toISOString(),
      endIso: new Date().toISOString(),
      dayCount: 1,
      isValid: false,
    };
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    startIso: start.toISOString(),
    endIso: end.toISOString(),
    dayCount: inclusiveLocalDayCount(range.startDate, range.endDate),
    isValid: true,
  };
}

/** Sum hourly stream heatmap cells into daily Sales / Orders / Spend totals keyed by date. */
export function extractDailyTotalsFromHeatmap(
  payload: Record<string, unknown> | undefined
): Map<string, DailyTotals> {
  const totalsByDate = new Map<string, DailyTotals>();
  if (!payload) return totalsByDate;

  const payloadHours = Array.isArray(payload.hours) ? payload.hours : [];
  for (const hourValue of payloadHours) {
    const hourEntry = hourValue as Record<string, unknown>;
    const rawCells = Array.isArray(hourEntry.cells) ? hourEntry.cells : [];
    for (const cellValue of rawCells) {
      const cell = cellValue as Record<string, unknown>;
      const date = String(cell.date || '').trim();
      if (!date) continue;
      const rowCount = numberValue(cell.rows);
      const aggregated = (cell.aggregated ?? {}) as Record<string, unknown>;
      const sales = numberValue(aggregated.sales);
      const spend = numberValue(aggregated.spend);
      const orders = numberValue(aggregated.units);
      if (rowCount <= 0 && sales === 0 && spend === 0 && orders === 0) continue;

      const existing = totalsByDate.get(date) ?? {
        sales: 0,
        orders: 0,
        spend: 0,
        hasData: false,
      };
      existing.sales += sales;
      existing.orders += orders;
      existing.spend += spend;
      existing.hasData = existing.hasData || rowCount > 0;
      totalsByDate.set(date, existing);
    }
  }
  return totalsByDate;
}

export function buildDailyPerformancePoints(
  payload: Record<string, unknown> | undefined,
  range: DateRange
): PerformanceDataPoint[] {
  const totalsByDate = extractDailyTotalsFromHeatmap(payload);
  return enumerateDatesInRange(range.startDate, range.endDate).map((date) => {
    const totals = totalsByDate.get(date);
    return {
      date,
      sales: totals?.sales ?? 0,
      orders: totals?.orders ?? 0,
      spend: totals?.spend ?? 0,
    };
  });
}

function weekStartIso(dateIso: string): string {
  const date = isoToLocalDate(dateIso);
  if (!date) return dateIso;
  const day = date.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + offset);
  return toIsoDate(date);
}

function monthStartIso(dateIso: string): string {
  const date = isoToLocalDate(dateIso);
  if (!date) return dateIso;
  return toIsoDate(new Date(date.getFullYear(), date.getMonth(), 1));
}

export function aggregatePerformancePoints(
  points: PerformanceDataPoint[],
  granularity: PerformanceGranularity
): PerformanceDataPoint[] {
  if (granularity === 'daily' || points.length === 0) return points;

  const buckets = new Map<string, PerformanceDataPoint>();
  for (const point of points) {
    const bucketDate =
      granularity === 'weekly' ? weekStartIso(point.date) : monthStartIso(point.date);
    const existing = buckets.get(bucketDate) ?? {
      date: bucketDate,
      sales: 0,
      orders: 0,
      spend: 0,
    };
    existing.sales += point.sales;
    existing.orders += point.orders;
    existing.spend += point.spend;
    buckets.set(bucketDate, existing);
  }

  return [...buckets.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export function performanceSeriesHasData(
  payload: Record<string, unknown> | undefined,
  points: PerformanceDataPoint[]
): boolean {
  const rowCount = numberValue(payload?.rowCount);
  if (rowCount > 0) return true;
  return points.some((point) => point.sales > 0 || point.orders > 0 || point.spend > 0);
}

export function buildPerformanceSeries(
  payload: Record<string, unknown> | undefined,
  range: DateRange,
  granularity: PerformanceGranularity
): { points: PerformanceDataPoint[]; hasData: boolean } {
  const dailyPoints = buildDailyPerformancePoints(payload, range);
  const points = aggregatePerformancePoints(dailyPoints, granularity);
  return {
    points,
    hasData: performanceSeriesHasData(payload, points),
  };
}
